/* eslint-disable @typescript-eslint/naming-convention */
import { Routes } from "@angular/router";
import { AutoLoginPartialRoutesGuard } from "angular-auth-oidc-client";

import { LandingPage } from "./pages/public/landing/landing.page";
// GUARDS
import { ConsumerBillingAdminGuard } from "./services/guards/consumer-guards/consumer-billing-admin.guard";
import { ConsumerExchangeAdminGuard } from "./services/guards/consumer-guards/consumer-exchange-admin.guard";
import { ConsumerGeneralUserGuard } from "./services/guards/consumer-guards/consumer-general-user.guard";
import { ConsumerSubAdminGuard } from "./services/guards/consumer-guards/consumer-sub-admin.guard";
import { ActiveUserGuard } from "./services/guards/general/active-user-guard";
import { IsLocalhostGuard } from "./services/guards/is-localhost.guard";
import { MarketplaceAdminGuard } from "./services/guards/marketplace-admin.guard";
import { MarketplaceAnyAdminGuard } from "./services/guards/marketplace-any-admin.guard";
import { MarketplaceSuperAdminGuard } from "./services/guards/marketplace-super-admin.guard";
import { UserManagementGuard } from "./services/guards/user-management.guard";
import { AddProposalGuard } from "./services/guards/vendor-guards/add-proposal.guard";
import { VendorBillingAdminGuard } from "./services/guards/vendor-guards/vendor-billing-admin.guard";
import { VendorGeneralUserGuard } from "./services/guards/vendor-guards/vendor-general-user.guard";

// GUARD GROUPS
const auth_guard: Array<any> = [AutoLoginPartialRoutesGuard];
const marketAdmin_guard: Array<any> = [MarketplaceAdminGuard];
const marketSuperAdmin_guard: Array<any> = [MarketplaceSuperAdminGuard];
const activeUser_guard: Array<any> = auth_guard.concat([ActiveUserGuard]);
const vendorBilling_guard: Array<any> = activeUser_guard.concat([VendorBillingAdminGuard]);
const consumerBillingAdmin_guard: Array<any> = activeUser_guard.concat([ConsumerBillingAdminGuard]);
const consumerExchangeAdmin_guard: Array<any> = activeUser_guard.concat([
	ConsumerExchangeAdminGuard
]);
const consumerSubAdmin_guard: Array<any> = activeUser_guard.concat([ConsumerSubAdminGuard]);
const consumerGeneralUser_guard: any[] = activeUser_guard.concat([ConsumerGeneralUserGuard]);
const vendorGeneralUser_guard: any[] = activeUser_guard.concat([VendorGeneralUserGuard]);
const userManagement_guard: any[] = activeUser_guard.concat([UserManagementGuard]);
const localhost_guard: any[] = [IsLocalhostGuard];

export const routerConfig: Routes = [
	// NO GUARD
	{
		path: "",
		component: LandingPage
	},
	{
		path: "auth-callback",
		loadComponent: () =>
			import("./services/auth/auth.callback.component").then(
				(mod) => mod.AuthCallbackComponent
			)
	},
	{
		path: "onboarding",
		loadComponent: () =>
			import("./pages/common/onboarding/onboarding.component").then(
				(mod) => mod.OnboardingComponent
			)
	},
	{
		path: "home",
		redirectTo: "app/dashboard",
		pathMatch: "full"
	},
	{
		path: "unauthorized",
		redirectTo: "app/dashboard",
		pathMatch: "full"
	},
	// MUST PASS login_guard
	{
		path: "app",
		loadComponent: () =>
			import("./portal-home.component").then((mod) => mod.PortalHomeComponent),
		canActivate: auth_guard,
		children: [
			{
				path: "",
				redirectTo: "dashboard",
				pathMatch: "full"
			},
			{
				path: "dashboard",
				loadComponent: () =>
					import("./dashboard-home.component").then((mod) => mod.DashboardHomeComponent),
				canActivate: activeUser_guard
			},
			{
				path: "pending",
				loadComponent: () =>
					import("./pages/common/pending-review/pending-review.page").then(
						(mod) => mod.PendingReviewPageComponent
					),
				canActivate: auth_guard
			},
			{
				path: "messaging",
				loadComponent: () =>
					import("./pages/common/messaging/messaging.page").then(
						(mod) => mod.MessagingPage
					),
				canActivate: activeUser_guard
			},
			{
				path: "about",
				loadComponent: () =>
					import("./pages/common/about/about.component").then(
						(mod) => mod.AboutComponent
					),
				canActivate: activeUser_guard
			},
			{
				path: "discover-vendors",
				loadComponent: () =>
					import("./pages/common/discover-vendors/discover-vendors-parent.page").then(
						(mod) => mod.DiscoverVendorsParentPage
					),
				children: [
					{
						path: "",
						loadComponent: () =>
							import(
								"./pages/common/discover-vendors/discover-vendors/discover-vendors.page"
							).then((mod) => mod.DiscoverVendorsPage),
						canActivate: activeUser_guard
					},
					{
						path: "vendor/:id",
						loadComponent: () =>
							import(
								"./pages/common/discover-vendors/vendor-profile/vendor-profile.page"
							).then((mod) => mod.VendorProfilePage),
						canActivate: activeUser_guard
					}
				]
			},
			{
				path: "reviews/:id",
				loadComponent: () =>
					import("./pages/common/reviews/reviews.component").then(
						(mod) => mod.ReviewsComponent
					),
				canActivate: activeUser_guard
			},
			{
				path: "proposals",
				loadComponent: () =>
					import(
						"./pages/common/proposals/proposals-outlet/proposals-outlet.component"
					).then((mod) => mod.ProposalsOutletComponent),
				canActivate: activeUser_guard,
				children: [
					{
						path: "",
						loadComponent: () =>
							import("./pages/common/proposals/proposals-home.component").then(
								(mod) => mod.ProposalsHomeComponent
							)
					},
					{
						path: ":id",
						loadComponent: () =>
							import(
								"./pages/common/proposals/proposal-details/view-proposal-details.component"
							).then((mod) => mod.ViewProposalDetailsComponent)
					},
				]
			},
			{
				path: "proposals-summary",
				loadComponent: () =>
					import(
						"./pages/common/proposals/proposal-payments/proposal-payments-home.component"
					).then((mod) => mod.ProposalPaymentsHomeComponent),
				canActivate: consumerGeneralUser_guard,
				children: [
					{
						path: "",
						loadComponent: () =>
							import(
								"./pages/common/proposals/proposal-payments/proposals-payments-summary-table.component"
							).then((mod) => mod.ProposalsPaymentsSummaryTableComponent)
					},
					{
						path: "proposal/:id",
						loadComponent: () =>
							import("./pages/common/finances/payments-table.component").then(
								(mod) => mod.PaymentsTableComponent
							)
					}
				]
			},
			{
				path: "exchange",
				loadComponent: () =>
					import(
						"./pages/vendor-pages/vendor-exchange/exchange-home/exchange-home.component"
					).then((mod) => mod.ExchangeHomeComponent),
				canActivate: vendorGeneralUser_guard,
				children: [
					{
						path: "",
						loadComponent: () =>
							import(
								"./pages/vendor-pages/vendor-exchange/vendor-exchange.component"
							).then((mod) => mod.VendorExchangeComponent)
					},
					{
						path: "order/:id",
						pathMatch: "full",
						redirectTo: ({ params }) => `/app/orders/${params.id as string}`
					},
					{
						path: "order/:id/add-proposal",
						canActivate: [AddProposalGuard],
						loadComponent: () =>
							import(
								"./pages/vendor-pages/vendor-proposals/add-proposal/add-proposal.component"
							).then((mod) => mod.AddProposalComponent)
					}
				]
			},
			{
				path: "vendor-reports",
				loadComponent: () =>
					import(
						"./pages/vendor-pages/vendor-exchange/vendor-reports-page.component"
					).then((mod) => mod.VendorReportsPageComponent),
				canActivate: vendorGeneralUser_guard
			},
			{
				path: "product-management",
				loadComponent: () =>
					import(
						"./pages/vendor-pages/vendor-account/my-products-page/my-products-page.component"
					).then((mod) => mod.MyProductsPageComponent),
				canActivate: vendorGeneralUser_guard
			},
			{
				path: "product-income",
				loadComponent: () =>
					import(
						"./pages/vendor-pages/vendor-finance/product-summary/product-summary-outlet/product-summary-outlet.component"
					).then((mod) => mod.ProductSummaryOutletComponent),
				canActivate: vendorGeneralUser_guard,
				children: [
					{
						path: ":product",
						loadComponent: () =>
							import(
								"./pages/vendor-pages/vendor-finance/product-summary/product-summary-home/product-summary-home.component"
							).then((mod) => mod.ProductSummaryHomeComponent)
					}
				]
			},
			{
				path: "user-management",
				loadComponent: () =>
					import("./pages/common/account-users/account-users.component").then(
						(mod) => mod.AccountUsersComponent
					),
				canActivate: userManagement_guard
			},
			{
				path: "accounts",
				loadComponent: () =>
					import(
						"./pages/admin-pages/account-info/consumer-activity-outlet/consumer-activity-outlet.component"
					).then((mod) => mod.ConsumerActivityOutletComponent),
				canActivate: marketAdmin_guard,
				children: [
					{
						path: "",
						loadComponent: () =>
							import("./pages/admin-pages/account-info/account-info.component").then(
								(mod) => mod.AccountInfoComponent
							)
					},
					{
						path: "consumer",
						canActivate: marketAdmin_guard,
						children: [
							{
								path: ":id",
								loadComponent: () =>
									import(
										"./pages/admin-pages/account-info/consumer-activity/consumer-activity.component"
									).then((mod) => mod.ConsumerActivityComponent),
								canActivate: marketAdmin_guard
							}
						]
					},
					{
						path: "vendor",
						canActivate: marketAdmin_guard,
						children: [
							{
								path: ":id",
								loadComponent: () =>
									import(
										"./pages/admin-pages/account-info/vendor-activity/vendor-activity.component"
									).then((mod) => mod.VendorActivityComponent),
								canActivate: marketAdmin_guard
							}
						]
					}
				]
			},
			{
				path: "add-user",
				loadComponent: () =>
					import("./pages/admin-pages/users/add-user.component").then(
						(mod) => mod.AddUserComponent
					),
				canActivate: marketSuperAdmin_guard
			},
			{
				path: "proposal-admin",
				loadComponent: () =>
					import("./pages/admin-pages/proposal/proposal-view.component").then(
						(mod) => mod.ProposalViewComponent
					),
				canActivate: [MarketplaceAnyAdminGuard],
				children: []
			},
			{
				path: "create-order",
				redirectTo: "purchase-order",
				pathMatch: "full"
			},
			{
				path: "purchase-order",
				loadComponent: () =>
					import("./pages/consumer-pages/purchase-order/purchase-order-page.component").then(
						(mod) => mod.PurchaseOrderPageComponent
					),
				canActivate: consumerSubAdmin_guard
			},
			{
				path: "orders",
				loadComponent: () =>
					import("./pages/consumer-pages/orders/orders-list.component").then(
						(mod) => mod.OrdersListComponent
					),
				canActivate: consumerSubAdmin_guard
			},
			{
				path: "orders/:id",
				loadComponent: () =>
					import("./pages/consumer-pages/orders/order-detail.component").then(
						(mod) => mod.OrderDetailComponent
					),
				data: { mode: "order" },
				canActivate: activeUser_guard
			},
			{
				path: "v2-templates",
				loadComponent: () =>
					import("./pages/consumer-pages/v2-templates/v2-templates-list.component").then(
						(mod) => mod.V2TemplatesListComponent
					),
				canActivate: consumerSubAdmin_guard
			},
			{
				// Registered before "v2-templates/:id" so "create" is not captured as an :id.
				path: "v2-templates/create",
				loadComponent: () =>
					import("./pages/consumer-pages/purchase-order/purchase-order-page.component").then(
						(mod) => mod.PurchaseOrderPageComponent
					),
				data: { mode: "template" },
				canActivate: consumerSubAdmin_guard
			},
			{
				path: "v2-templates/:id/edit",
				loadComponent: () =>
					import("./pages/consumer-pages/purchase-order/purchase-order-page.component").then(
						(mod) => mod.PurchaseOrderPageComponent
					),
				data: { mode: "template" },
				canActivate: consumerSubAdmin_guard
			},
			{
				path: "v2-templates/:id/quick-order",
				loadComponent: () =>
					import("./pages/consumer-pages/v2-templates/quick-order/quick-order-page.component").then(
						(mod) => mod.QuickOrderPageComponent
					),
				canActivate: consumerSubAdmin_guard
			},
			{
				path: "v2-templates/:id",
				loadComponent: () =>
					import("./pages/consumer-pages/orders/order-detail.component").then(
						(mod) => mod.OrderDetailComponent
					),
				data: { mode: "template" },
				canActivate: consumerSubAdmin_guard
			},
			{
				path: "v2-vendor-groups",
				loadComponent: () =>
					import("./pages/consumer-pages/vendor-groups/v2-vendor-groups-page.component").then(
						(mod) => mod.V2VendorGroupsPageComponent
					),
				canActivate: consumerSubAdmin_guard
			},
			{
				path: "v2-reports",
				loadComponent: () =>
					import("./pages/consumer-pages/orders/reports-page.component").then(
						(mod) => mod.ReportsPageComponent
					),
				canActivate: consumerSubAdmin_guard
			},
			{
				path: "feedback",
				loadComponent: () =>
					import("./pages/common/feedback/feedback-home.component").then(
						(mod) => mod.FeedbackHomeComponent
					),
				canActivate: activeUser_guard,
				children: [
					{
						path: "",
						loadComponent: () =>
							import("./pages/common/feedback/feedback-list.component").then(
								(mod) => mod.FeedbackListComponent
							),
						canActivate: activeUser_guard
					}
				]
			},
			{
				path: "announcements",
				loadComponent: () =>
					import("./pages/common/announcement/announcement-home.component").then(
						(mod) => mod.AnnouncementHomeComponent
					),
				children: [
					{
						path: "",
						loadComponent: () =>
							import(
								"./pages/common/announcement/announcement-admin-list.component"
							).then((mod) => mod.AnnouncementAdminListComponent),
						canActivate: marketAdmin_guard
					},
					{
						path: "full-list",
						loadComponent: () =>
							import(
								"./pages/common/announcement/announcement-full-list.component"
							).then((mod) => mod.AnnouncementFullListComponent),
						canActivate: activeUser_guard
					}
				]
			},
			{
				path: "account-settings",
				loadComponent: () =>
					import("./pages/common/account/account-home/account-home.component").then(
						(mod) => mod.AccountHomeComponent
					),
				canActivate: activeUser_guard
			},
			{
				path: "v2-orders",
				redirectTo: "orders",
				pathMatch: "full"
			},
			{
				path: "v2-orders/:id",
				pathMatch: "full",
				redirectTo: ({ params }) => `/app/orders/${params.id as string}`
			},
			{
				path: "efforts",
				loadComponent: () =>
					import("./pages/consumer-pages/consumer-efforts/efforts-home.component").then(
						(mod) => mod.EffortsHomeComponent
					),
				canActivate: consumerGeneralUser_guard,
				children: [
					{
						path: "",
						loadComponent: () =>
							import(
								"./pages/consumer-pages/consumer-efforts/efforts-table.component"
							).then((mod) => mod.EffortsTableComponent)
					},
					{
						path: "create",
						loadComponent: () =>
							import(
								"./pages/consumer-pages/consumer-efforts/effort-form.component"
							).then((mod) => mod.EffortFormComponent)
					},
					{
						path: ":id",
						loadComponent: () =>
							import(
								"./pages/consumer-pages/consumer-efforts/effort-details.component"
							).then((mod) => mod.EffortDetailsComponent)
					}
				]
			},
			{
				path: "contract-management",
				loadComponent: () =>
					import("./pages/admin-pages/finances/contracts.component").then(
						(mod) => mod.ContractsComponent
					),
				canActivate: marketAdmin_guard
			},
			{
				path: "finance-management",
				loadComponent: () =>
					import("./pages/common/finances/finance-outlet.component").then(
						(mod) => mod.FinanceOutletComponent
					),
				canActivate: activeUser_guard,
				children: [
					{
						path: "",
						loadComponent: () =>
							import("./pages/common/finances/finances.component").then(
								(mod) => mod.FinancesComponent
							),
						children: [
							{
								path: "contract-consumer",
								loadComponent: () =>
									import(
										"./pages/admin-pages/contract-consumer/contract-consumer-home.component"
									).then((mod) => mod.ContractConsumerHomeComponent),
								canActivate: marketSuperAdmin_guard,
								children: [
									{
										path: ":id/fund",
										loadComponent: () =>
											import(
												"./pages/admin-pages/contract-consumer/contract-consumer-funding.component"
											).then((mod) => mod.ContractConsumerFundingComponent),
										canActivate: marketSuperAdmin_guard
									},
									{
										path: ":id/funding-history",
										loadComponent: () =>
											import(
												"./pages/admin-pages/contract-consumer/contract-consumer-funding-history.component"
											).then(
												(mod) => mod.ContractConsumerFundingHistoryComponent
											),
										canActivate: marketSuperAdmin_guard
									},
									{
										path: ":id/itf",
										loadComponent: () =>
											import(
												"./pages/admin-pages/contract-consumer/itf.component"
											).then((mod) => mod.AdminItfComponent),
										canActivate: marketSuperAdmin_guard
									}
								]
							},
							{
								path: "payments",
								loadComponent: () =>
									import(
										"./pages/admin-pages/finances/admin-payments.component"
									).then((mod) => mod.AdminPaymentsComponent),
								canActivate: marketAdmin_guard
							},
							{
								path: "invoices",
								canActivate: marketAdmin_guard,
								loadComponent: () =>
									import("./pages/admin-pages/finances/invoicing.component").then(
										(mod) => mod.InvoicingComponent
									),
								children: [
									{
										path: ":id",
										loadComponent: () =>
											import(
												"./pages/admin-pages/finances/invoice-details.component"
											).then((mod) => mod.InvoiceDetailsComponent),
										canActivate: marketAdmin_guard
									}
								]
							},
							{
								path: "reports",
								loadComponent: () =>
									import("./pages/admin-pages/finances/reports.component").then(
										(mod) => mod.ReportsComponent
									),
								canActivate: marketAdmin_guard,
								children: [
									{
										path: "projection",
										loadComponent: () =>
											import(
												"./pages/admin-pages/finances/admin-projection-component"
											).then((mod) => mod.AdminProjectionComponent),
										canActivate: marketAdmin_guard
									},
									{
										path: "order-reports",
										loadComponent: () =>
											import(
												"./pages/common/finances/funding-reports-component"
											).then((mod) => mod.FundingReportsComponent),
										canActivate: marketAdmin_guard
									},
									{
										path: "payment-reconciliation",
										loadComponent: () =>
											import(
												"./pages/admin-pages/finances/payment-reconciliation.component"
											).then((mod) => mod.PaymentReconciliationComponent),
										canActivate: marketAdmin_guard
									},
									{
										path: "consumer-ledger-discrepancies",
										loadComponent: () =>
											import(
												"./pages/admin-pages/finances/consumer-ledger-discrepancies"
											).then((mod) => mod.ConsumerLedgerDiscrepancies),
										canActivate: marketAdmin_guard
									},
									{
										path: "canceled-proposals-with-balances",
										loadComponent: () =>
											import(
												"./pages/admin-pages/finances/canceled-proposals-with-balances"
											).then((mod) => mod.CanceledProposalsWithBalances),
										canActivate: marketAdmin_guard
									},
									{
										path: "proposal-stats",
										loadComponent: () =>
											import(
												"./pages/admin-pages/finances/proposal-stats.component"
											).then((mod) => mod.ProposalStatsComponent),
										canActivate: marketAdmin_guard
									}
								]
							},
							{
								path: "revenue",
								loadComponent: () =>
									import(
										"./pages/admin-pages/finances/revenue-recognition/revenue-recognition.component"
									).then((mod) => mod.RevenueRecognitionComponent),
								canActivate: marketAdmin_guard
							},
							{
								path: "projection",
								loadComponent: () =>
									import(
										"./pages/admin-pages/finances/admin-projection-component"
									).then((mod) => mod.AdminProjectionComponent),
								canActivate: marketAdmin_guard
							},
							{
								path: "payment-reconciliation",
								loadComponent: () =>
									import(
										"./pages/admin-pages/finances/payment-reconciliation.component"
									).then((mod) => mod.PaymentReconciliationComponent),
								canActivate: marketAdmin_guard
							},
							{
								path: "fx-gates",
								loadComponent: () =>
									import(
										"./pages/admin-pages/finances/fx-gates/fx-gates.component"
									).then((mod) => mod.FxGatesComponent),
								canActivate: marketAdmin_guard
							},
							{
								path: "payout-issues",
								loadComponent: () =>
									import(
										"./pages/admin-pages/finances/payout-issues/payout-issues.component"
									).then((mod) => mod.PayoutIssuesComponent),
								canActivate: marketAdmin_guard
							}
						]
					},
					{
						path: "ach-beneficial-owner-document/:id",
						loadComponent: () =>
							import(
								"./pages/consumer-pages/consumer-finance/ach-bo-document/ach-bo-document.component"
							).then((mod) => mod.AchBoDocumentComponent),
						canActivate: consumerBillingAdmin_guard
					},
					{
						path: "ach-beneficial-owner-retry/:id",
						loadComponent: () =>
							import(
								"./pages/consumer-pages/consumer-finance/ach-bo-retry/ach-bo-retry.component"
							).then((mod) => mod.AchBoRetryComponent),
						canActivate: consumerBillingAdmin_guard
					},
					{
						path: "ach-document/:type",
						loadComponent: () =>
							import(
								"./pages/consumer-pages/consumer-finance/ach-document/ach-document.component"
							).then((mod) => mod.AchDocumentComponent),
						canActivate: consumerBillingAdmin_guard
					},
					{
						path: "ach-issues",
						loadComponent: () =>
							import(
								"./pages/consumer-pages/consumer-finance/ach-issues/ach-issues.component"
							).then((mod) => mod.AchIssuesComponent),
						canActivate: consumerBillingAdmin_guard
					},
					{
						path: "ach-retry",
						loadComponent: () =>
							import(
								"./pages/consumer-pages/consumer-finance/ach-retry/ach-retry.component"
							).then((mod) => mod.AchRetryComponent),
						canActivate: consumerBillingAdmin_guard
					},
					{
						path: "add-bank-account",
						loadComponent: () =>
							import(
								"./pages/vendor-pages/vendor-finance/vendor-add-bank/vendor-add-bank.component"
							).then((mod) => mod.VendorAddBankComponent),
						canActivate: vendorBilling_guard
					},
					{
						path: "add-us-bank-account",
						loadComponent: () =>
							import(
								"./components/ach-account-form/vendor-ach-account-form.component"
							).then((mod) => mod.VendorAchAccountFormComponent),
						canActivate: vendorBilling_guard
					},
					{
						path: "add-intl-bank-account",
						loadComponent: () =>
							import(
								"./pages/vendor-pages/vendor-finance/vendor-intl-bank-account/vendor-intl-bank-account-page.component"
							).then((mod) => mod.VendorIntlBankAccountPageComponent),
						canActivate: vendorBilling_guard
					},
					{
						path: "add-funds",
						loadComponent: () =>
							import(
								"./pages/consumer-pages/consumer-finance/add-funds/add-funds.component"
							).then((mod) => mod.AddFundsComponent),
						canActivate: consumerBillingAdmin_guard
					},
					{
						path: "allocate-funds",
						pathMatch: "full",
						redirectTo: () => "/app/proposals?tab=allocate-funds"
					},
					{
						path: "add-funding-source",
						loadComponent: () =>
							import(
								"./pages/consumer-pages/consumer-finance/add-funding-source/add-funding-source.component"
							).then((mod) => mod.AddFundingSourceComponent),
						canActivate: consumerBillingAdmin_guard
					},
					{
						path: "credit-card",
						loadComponent: () =>
							import(
								"./pages/consumer-pages/consumer-finance/auth-net/authnet-iframe.component"
							).then((mod) => mod.AuthnetIframeComponent),
						canActivate: consumerBillingAdmin_guard
					},
					{
						path: "ach-apply",
						loadComponent: () =>
							import(
								"./pages/consumer-pages/consumer-finance/ach-account-apply/ach-account-apply.component"
							).then((mod) => mod.AchAccountApplyComponent),
						canActivate: consumerBillingAdmin_guard
					}
				]
			},
			{
				path: "account-requests",
				loadComponent: () =>
					import("./pages/admin-pages/account-requests/account-requests.page").then(
						(mod) => mod.AccountRequestsPage
					),
				canActivate: marketAdmin_guard,
				children: [
					{
						path: ":id/compliance",
						loadComponent: () =>
							import(
								"./pages/admin-pages/account-requests/account-details.page"
							).then((mod) => mod.AccountDetailsPage),
						canActivate: marketAdmin_guard
					}
				]
			},
			{
				path: "products",
				loadComponent: () =>
					import(
						"./pages/consumer-pages/products/products-outlet/products-outlet.component"
					).then((mod) => mod.ProductsOutletComponent),
				canActivate: consumerExchangeAdmin_guard,
				children: [
					{
						path: ":product",
						loadComponent: () =>
							import(
								"./pages/consumer-pages/products/products-home/products-home.component"
							).then((mod) => mod.ProductsHomeComponent),
						canActivate: consumerExchangeAdmin_guard
					}
				]
			},
			{
				path: "excess-funds",
				loadComponent: () =>
					import("./pages/admin-pages/excess-funds/excess-funds.component").then(
						(mod) => mod.ExcessFundsComponent
					),
				canActivate: marketAdmin_guard
			},
			{
				path: "product-admin",
				loadComponent: () =>
					import("./pages/admin-pages/products/products.component").then(
						(mod) => mod.ProductsComponent
					),
				canActivate: marketAdmin_guard,
				children: [
					{
						path: "",
						loadComponent: () =>
							import(
								"./pages/admin-pages/products/views/families-view.component"
							).then((mod) => mod.FamiliesViewComponent)
					},
					{
						path: "standards",
						loadComponent: () =>
							import(
								"./pages/admin-pages/products/views/standards-view.component"
							).then((mod) => mod.StandardsViewComponent)
					},
					{
						path: "monitoring",
						loadComponent: () =>
							import(
								"./pages/admin-pages/products/views/monitoring-view.component"
							).then((mod) => mod.MonitoringViewComponent)
					},
					{
						path: "data-rights",
						loadComponent: () =>
							import(
								"./pages/admin-pages/products/views/data-rights-view.component"
							).then((mod) => mod.DataRightsViewComponent)
					},
					{
						path: "export-compliance",
						loadComponent: () =>
							import(
								"./pages/admin-pages/products/views/export-compliance-view.component"
							).then((mod) => mod.ExportComplianceViewComponent)
					},
					{
						path: "order-preview",
						loadComponent: () =>
							import(
								"./pages/admin-pages/products/views/families-view.component"
							).then((mod) => mod.FamiliesViewComponent)
					},
					{
						path: "family/:familyId",
						loadComponent: () =>
							import(
								"./pages/admin-pages/products/views/family-detail-view.component"
							).then((mod) => mod.FamilyDetailViewComponent)
					},
					{
						path: "family/:familyId/category/:categoryId",
						loadComponent: () =>
							import(
								"./pages/admin-pages/products/views/category-detail-view.component"
							).then((mod) => mod.CategoryDetailViewComponent)
					},
					{
						path: "family/:familyId/product/:productId",
						loadComponent: () =>
							import(
								"./pages/admin-pages/products/views/product-detail-view.component"
							).then((mod) => mod.ProductDetailViewComponent)
					},
					{
						path: "family/:familyId/category/:categoryId/product/:productId",
						loadComponent: () =>
							import(
								"./pages/admin-pages/products/views/product-detail-view.component"
							).then((mod) => mod.ProductDetailViewComponent)
					}
				]
			},
			{
				path: "i18n-audit",
				loadComponent: () =>
					import("./pages/admin-pages/i18n-audit/i18n-audit.component").then(
						(mod) => mod.I18nAuditComponent
					),
				canActivate: marketAdmin_guard
			},
			{
				path: "analytics",
				loadComponent: () =>
					import("./pages/common/analytics/analytics.component").then(
						(mod) => mod.AnalyticsComponent
					),
				canActivate: activeUser_guard
			},
			{
				path: "help",
				loadComponent: () =>
					import("./pages/common/help/help.component").then((mod) => mod.HelpComponent)
			},
			{
				path: "api-docs",
				loadComponent: () =>
					import("./pages/common/swagger-api/swagger-api.page").then(
						(mod) => mod.SwaggerApiPage
					)
			},
			{
				path: "analytics-exclusion",
				loadComponent: () =>
					import(
						"./pages/admin-pages/analytics-exclusion/analytics-exclusion.component"
					).then((mod) => mod.AnalyticsExclusionComponent),
				canActivate: marketSuperAdmin_guard
			},
			{
				path: "agreements",
				loadComponent: () =>
					import("./pages/admin-pages/agreements/legal-file-config.component").then(
						(mod) => mod.LegalFileConfigComponent
					),
				canActivate: marketAdmin_guard
			},
			{
				path: "catalogue",
				loadComponent: () =>
					import("./pages/common/catalogue/catalogue.component").then(
						(mod) => mod.CatalogueComponent
					),
				canActivate: activeUser_guard
			},
			{
				path: "localhost-sandbox",
				loadComponent: () =>
					import("./pages/localhost/components.page").then(
						(mod) => mod.ComponentsPageComponent
					),
				canActivate: localhost_guard
			}
		]
	},
	{
		path: "**",
		loadComponent: () =>
			import("./pages/public/not-found/not-found.component").then(
				(mod) => mod.NotFoundComponent
			)
	}
];
